import { format } from "date-fns";
import { getEmptyBoneLoss } from "duck/utils/cases";
import generateId from "duck/utils/generateId";
import {
  MedicationCalculationTypeEnum,
  PatientTestEnum,
  RouteEnum,
  VaccineEnum,
  WeightMeasureEnum
} from "requests/generated";

export const EMPTY_CASE = {
  client: {
    accountNumber: null,
    email: null,
    firstName: null,
    lastName: null,
    phoneFirst: null,
    phoneSecond: null,
    notAvailableTime: null,
    streetAddress: null,
    apartments: null,
    disclaimer: {
      lifeSavingMeasure: null,
      signature: null,
      createdAt: null
    },
    city: null,
    state: null,
    zipCode: null,

    patient: {
      image: null,
      name: null,
      sex: null,
      species: null,
      breed: null,
      dateOfBirth: null,
      weight: null,
      weightMeasure: WeightMeasureEnum.Lbs,
      procedureDate: null,
      alerts: null,
      procedures: null,

      dropOff: {
        admittingConcern: null,
        evaluateTestAndTreat: false,
        doctor: null,
        performInitialEvaluation: false
      },

      authorization: {
        administrationOfAntiNauseaMedication: false,
        placementOfMicrochip: false,
        postOperativeLaserTherapy: false,
        needElizabethanCollar: false
      },

      vaccines: [],
      patientTests: [],
      treatment: null,

      examNote: {
        additionalNotes: null,
        allergies: null,
        bloodwork: null,
        lastMealAt: null,
        patientPersonalItems: null,
        previousSurgery: null,
        treatmentPlan: null,
        vaccines: null
      },

      preAnestheticExam: {
        temperature: null,
        heartRate: null,
        respiratoryRate: null,
        painScore: null,
        bodyConditionScore: null,
        dentalScore: null,
        physicalStatus: null
      },
      physicalExam: {
        generalAppearance: null,
        gastrointestinal: null,
        oralCavity: null,
        musculosceletal: null,
        mucousMembranes: null,
        lymphNodes: null,
        eyes: null,
        urogenital: null,
        ears: null,
        integumentary: null,
        cardiovascular: null,
        nervousSystem: null,
        respiratory: null,
        generalAppearanceAbnormalStateComments: null,
        gastrointestinalAbnormalStateComments: null,
        oralCavityAbnormalStateComments: null,
        musculosceletalAbnormalStateComments: null,
        mucousMembranesAbnormalStateComments: null,
        lymphNodesAbnormalStateComments: null,
        eyesAbnormalStateComments: null,
        urogenitalAbnormalStateComments: null,
        earsAbnormalStateComments: null,
        integumentaryAbnormalStateComments: null,
        cardiovascularAbnormalStateComments: null,
        nervousSystemAbnormalStateComments: null,
        respiratoryAbnormalStateComments: null
      },
      currentMedications: [],
      diagnosticTesting: {
        abnormalitiesAndNotes: null,
        reviewedByDvm: null,
        additionalTests: [
          { test: "CBC", toPerform: false, fakeId: generateId() },
          { test: "Chemistry", toPerform: false, fakeId: generateId() }
        ],
        doctor: null
      },
      medicationCalculations: [
        {
          medication: "Epi Low",
          fakeId: generateId(),
          dose: 0.01,
          calculationType: MedicationCalculationTypeEnum.Arrest,
          concentration: 1,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Epi High",
          fakeId: generateId(),
          dose: 0.1,
          calculationType: MedicationCalculationTypeEnum.Arrest,
          concentration: 1,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Vasopressin",
          fakeId: generateId(),
          dose: 0.8,
          calculationType: MedicationCalculationTypeEnum.Arrest,
          concentration: 20,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Atropine",
          fakeId: generateId(),
          dose: 0.04,
          calculationType: MedicationCalculationTypeEnum.Arrest,
          concentration: 0.54,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Amiodarone",
          fakeId: generateId(),
          dose: 5,
          calculationType: MedicationCalculationTypeEnum.AntiArrhythmic,
          concentration: 50,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Lidocaine",
          fakeId: generateId(),
          dose: 2,
          calculationType: MedicationCalculationTypeEnum.AntiArrhythmic,
          concentration: 20,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Naloxone",
          fakeId: generateId(),
          dose: 0.04,
          calculationType: MedicationCalculationTypeEnum.Reversal,
          concentration: 0.4,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Flumazenil",
          fakeId: generateId(),
          dose: 0.01,
          calculationType: MedicationCalculationTypeEnum.Reversal,
          concentration: 0.1,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        },
        {
          medication: "Atipamezole",
          fakeId: generateId(),
          dose: 0.1,
          calculationType: MedicationCalculationTypeEnum.Reversal,
          concentration: 5,
          route: RouteEnum.Iv,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          initials: null
        }
      ],
      miscellaneousGroup: {
        catheterSize: null,
        catheterLocation: null,
        fluidType: null,
        bloodPressureMonitoring: null,
        fluidRate: null,
        fluidRateValue: null,
        qualityOfSedation: null,
        anesthesiaSystem: null,
        bagSize: null,
        oxygenFlowRate: null,
        intubated: null,
        endotrachealTubeSize: null
      },
      defibMedications: [
        {
          fakeId: generateId(),
          doseMax: 6,
          doseMin: 4,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          medication: "External Defib",
          initials: null
        },
        {
          fakeId: generateId(),
          doseMax: 1,
          doseMin: 0.5,
          timeAdministered: format(new Date(), "k:mm:ssxxx"),
          medication: "Internal Defib",
          initials: null
        }
      ],
      fluidBolus: null,
      calculationAgreement: null,
      regionalBlocks: [],
      tablets: [],
      suspensions: [],
      tibialPlateauLevelingOsteotomyPlanning: null,
      postAnestheticVitals: [],
      postAnestheticVitalComment: {
        comment: null,
        surgeryReport: null
      },
      vitalComment: {
        comment: null,
        qualityRecovery: null,
        timeExtubation: null
      },
      monitoring: {
        monitoringBy: null
      },
      patientProcedures: [
        {
          name: "Anesthesia",
          startTime: null,
          endTime: null,
          fakeId: generateId()
        }
      ],
      fluidTemperatures: [],
      anestheticGases: [],
      vitals: [],
      generalAssessment: null,
      dentalChartings: null,
      dentalSketch: null,
      dentalSketchComments: []
    }
  }
};

export const CAT_VACCINES = [
  VaccineEnum.Rabies,
  VaccineEnum.Fvrcp,
  VaccineEnum.FelineLeukemia
];

export const DOG_VACCINES = [
  VaccineEnum.Rabies,
  VaccineEnum.Da2pp,
  VaccineEnum.Leptospirosis,
  VaccineEnum.Bordatella,
  VaccineEnum.CanineInfluenza
];

export const CAT_TESTS = [
  PatientTestEnum.FelvFiv,
  PatientTestEnum.Fecal,
  PatientTestEnum.UrineAnalysis
];

export const DOG_TESTS = [
  PatientTestEnum.Heartworm,
  PatientTestEnum.Fecal,
  PatientTestEnum.UrineAnalysis
];

export const EMPTY_GENERAL_ASSESSMENT = {
  buccalMucosa: null,
  caudalOralMucosa: null,
  dentalChart: null,
  extraoral: null,
  fistula: null,
  hardPalate: null,
  lymphNodes: null,
  occlusion: null,
  occlusionDescription: "",
  parulisSinusTract: null,
  skullType: null,
  softPalate: null,
  tongue: null,
  tonsils: null,
  periodontalDisease: null,
  plaqueIndex: null,
  calculusIndex: null,
  gingivalIndex: null,
  additionalCommentsAndNotes: "",
  notesTimer: "",
  intraoralRadiographicComments: "",
  cbctComments: "",
  referralNotesAndComments: "",
  notesForClientDentalReport: "",
  rightRegionalBlocks: [],
  rightInfraorbital: null,
  rightMaxillary: null,
  rightRostralMandibular: null,
  rightInferiorAlveolar: null,
  leftRegionalBlocks: [],
  leftInfraorbital: null,
  leftMaxillary: null,
  leftRostralMandibular: null,
  leftInferiorAlveolar: null,
  oralSurgeries: [],
  orthodontics: [],
  periodontics: [],
  planingAndSubgingivalCurettage: null,
  boneAugmentation: false,
  boneAugmentationDetails: null,
  treatmentAdditionalFindingsCommentsAndNotes: "",
  between25And50PerCentBoneLoss: null,
  externalCervicalToothResorption: null,
  externalInflammatoryToothResorption: null,
  externalReplacementRootResorption: null,
  externalSurfaceRootResorption: null,
  greaterThan50PerCentBoneLoss: null,
  internalToothResorption: null,
  lessThan25PerCentBoneLoss: null,
  missingTeeth: null,
  periapicalLucency: null,
  periapicalRarefaction: null,
  retainedToothRoots: null,
  toothResorptionType1: null,
  toothResorptionType2: null,
  toothResorptionType3: null,
  uneruptedTeeth: null,
  widenedPulpCanal: null,
  beforePhotoFront: null,
  beforePhotoLeft: null,
  beforePhotoRight: null,
  afterPhotoFront: null,
  afterPhotoLeft: null,
  afterPhotoRight: null,
  additionalPhotos: [],
  radiographicFindingsPhotos: [],
  ...getEmptyBoneLoss()
};

export const EMPTY_DENTAL_CHARTING = {
  treatmentComment: "",
  toothState: null,
  periodontal: null,
  furcation: null,
  mobility: null,
  gingivitis: null,
  calculus: null,
  enamel: null,
  fracture: null,
  resorption: null,
  other: [],
  timerDurationSec: 0,
  notesTimer: "",
  periodontalPocketMBuc: null,
  periodontalPocketBuccal: null,
  periodontalPocketDBuc: null,
  periodontalPocketMLingPal: null,
  periodontalPocketLingPal: null,
  periodontalPocketDLingPal: null,
  gingivalRecessionMBuc: null,
  gingivalRecessionBuccal: null,
  gingivalRecessionDBuc: null,
  gingivalRecessionMLingPal: null,
  gingivalRecessionLingPal: null,
  gingivalRecessionDLingPal: null,
  oralSurgeries: [],
  sutureMaterial: null,
  restorations: [],
  restoratives: null,
  crCementation: null,
  endodontics: [],
  obturation: null,
  distalWorkingLength: null,
  mesialWorkingLength: null,
  palatalWorkingLength: null,
  distalMarFile: null,
  mesialMarFile: null,
  palatalMarFile: null,
  technique: ""
};
